export class ImageGalleryController {
    constructor(chatController) {
        this.chatController = chatController;
        this.imageModal = new bootstrap.Modal(document.getElementById('imageModal'));
        this.modalImage = document.querySelector('#imageModal .modal-body img');
        this.galleryContainer = document.querySelector('.image-gallery');
        this.currentCharacterName = '';
        this.isLoading = false;
        
        // Listen for museum open event
        document.addEventListener('openMuseum', this.handleMuseumOpen.bind(this));
        
        // Listen for character change events
        document.addEventListener('characterSelected', (event) => {
            if (event.detail && event.detail.name) {
                this.currentCharacterName = event.detail.name;
                // Only auto-load images if the museum panel is visible
                const museumPanel = document.getElementById('image-gallery-panel');
                if (museumPanel && museumPanel.style.display === 'block') {
                    this.loadImagesForCharacter(this.currentCharacterName);
                }
            }
        });
        
        this.bindEvents();
    }

    bindEvents() {
        // Bind events for static elements
        this.bindStaticEvents();
        
        // Add refresh button event
        const refreshBtn = document.getElementById('refresh-images-btn');
        if (refreshBtn) {
            refreshBtn.addEventListener('click', () => {
                if (this.currentCharacterName) {
                    this.loadImagesForCharacter(this.currentCharacterName, true);
                }
            });
        }
    }
    
    bindStaticEvents() {
        // Zoom icon clicks
        document.querySelectorAll('.zoom-icon').forEach(icon => {
            icon.addEventListener('click', (e) => {
                e.stopPropagation();
                const img = e.target.closest('.gallery-item').querySelector('img');
                this.showLargeImage(img.src, img.alt);
            });
        });

        // Send to chat buttons
        document.querySelectorAll('.send-to-chat').forEach(button => {
            button.addEventListener('click', (e) => {
                e.stopPropagation();
                const description = e.target.closest('.gallery-item').dataset.description;
                this.sendToChat(description);
            });
        });

        // Clicking gallery item shows description
        document.querySelectorAll('.gallery-item').forEach(item => {
            item.addEventListener('click', () => {
                const description = item.dataset.description;
                this.showDescription(description);
            });
        });
    }
    
    // Called when the museum panel is opened
    handleMuseumOpen() {
        console.log('Museum opened');

        // Scholar's Journal: record a museum visit (legend defaults to active).
        try { window.logJournalActivity?.('museum_view', {}); } catch (e) {}
        
        // Check if we have a current character
        if (this.currentCharacterName) {
            this.loadImagesForCharacter(this.currentCharacterName);
        } else {
            // Get the current character from the model ID if available
            const currentModelInput = document.getElementById('character-model');
            if (currentModelInput && currentModelInput.value) {
                const modelId = currentModelInput.value;
                // Look up the character name from the model ID
                const characterItems = document.querySelectorAll('.character-item');
                for (const item of characterItems) {
                    if (item.dataset.modelId === modelId) {
                        const nameElement = item.querySelector('.character-name');
                        if (nameElement) {
                            this.currentCharacterName = nameElement.textContent;
                            this.loadImagesForCharacter(this.currentCharacterName);
                            break;
                        }
                    }
                }
            }
            
            // If we still don't have a character name, use a default
            if (!this.currentCharacterName) {
                this.currentCharacterName = 'Richard Feynman';  // Default character
                this.loadImagesForCharacter(this.currentCharacterName);
            }
        }
    }
    
    // Load images for a character
    async loadImagesForCharacter(characterName, refresh = false, customDescription = null) {
        if (!characterName || this.isLoading) return;
        
        this.isLoading = true;
        this.showLoadingState();
        
        try {
            console.log(`Loading images for: ${characterName}`);
            
            // Build API URL with parameters - show more images by default
            let url = `/api/images/search?character=${encodeURIComponent(characterName)}&count=20`;
            
            // Add refresh parameter if needed
            if (refresh) {
                url += '&refresh=true';
            }
            
            // Add custom description if provided
            if (customDescription) {
                url += `&description=${encodeURIComponent(customDescription)}`;
            }
            
            // AI-enhanced descriptions are always enabled by default now
            url += '&enhance_descriptions=true';
            
            console.log(`Requesting images with URL: ${url}`);
            const response = await fetch(url);
            
            if (!response.ok) {
                throw new Error(`API request failed with status: ${response.status}`);
            }
            
            const data = await response.json();
            
            if (data.status === 'error') {
                throw new Error(data.error || 'Unknown error occurred');
            }
            
            // Update the gallery with new images
            this.updateGallery(data);
            
        } catch (error) {
            console.error('Error loading images:', error);
            this.showErrorState(error.message);
        } finally {
            this.isLoading = false;
        }
    }
    
    // Show loading state in the gallery
    showLoadingState() {
        if (!this.galleryContainer) return;
        
        this.galleryContainer.innerHTML = `
            <div class="loading-container">
                <div class="spinner-border text-light" role="status">
                    <span class="visually-hidden">Loading...</span>
                </div>
                <p class="mt-3">Loading images...</p>
            </div>
        `;
    }
    
    // Show error state in the gallery
    showErrorState(message) {
        if (!this.galleryContainer) return;
        
        this.galleryContainer.innerHTML = `
            <div class="error-container text-center">
                <div class="error-icon mb-3">
                    <i class="fas fa-exclamation-triangle fa-3x"></i>
                </div>
                <p>Error loading images: ${message}</p>
                <button id="retry-load-images" class="btn btn-outline-light mt-3">
                    <i class="fas fa-sync-alt"></i> Retry
                </button>
            </div>
        `;
        
        // Add retry button event
        const retryBtn = document.getElementById('retry-load-images');
        if (retryBtn) {
            retryBtn.addEventListener('click', () => {
                if (this.currentCharacterName) {
                    this.loadImagesForCharacter(this.currentCharacterName);
                }
            });
        }
    }
    
    // Update the gallery with new images
    updateGallery(data) {
        if (!this.galleryContainer) return;
        
        // Clear existing content
        this.galleryContainer.innerHTML = '';
        
        // Add title with character name and refresh button
        const titleRow = document.createElement('div');
        titleRow.className = 'gallery-title-row';
        titleRow.innerHTML = `
            <h4 class="gallery-title">${data.character_name || 'Character'} Images</h4>
            <button id="refresh-images-btn" class="btn btn-sm btn-outline-light">
                <i class="fas fa-sync-alt"></i>
            </button>
        `;
        this.galleryContainer.appendChild(titleRow);
        
        // Add search options
        const searchOptions = document.createElement('div');
        searchOptions.className = 'gallery-search-options mb-3';
        
        // AI-enhanced descriptions are always used now
        searchOptions.innerHTML = `
            <div class="input-group mb-3">
                <input type="text" id="custom-description-input" class="form-control form-control-sm" 
                    placeholder="Search for achievements, inventions, or life events (e.g., '${data.character_name} Nobel Prize')">
                <button id="search-custom-btn" class="btn btn-sm btn-primary">Find Images</button>
            </div>
        `;
        this.galleryContainer.appendChild(searchOptions);
        
        // Bind custom search events
        const searchCustomBtn = document.getElementById('search-custom-btn');
        const customDescInput = document.getElementById('custom-description-input');
        
        if (searchCustomBtn && customDescInput) {
            // Custom search button click
            searchCustomBtn.addEventListener('click', () => {
                const customDesc = customDescInput.value.trim();
                
                if (this.currentCharacterName) {
                    this.loadImagesForCharacter(
                        this.currentCharacterName, 
                        true, // refresh
                        customDesc || null
                    );
                }
            });
            
            // Handle Enter key in custom description input
            customDescInput.addEventListener('keypress', (e) => {
                if (e.key === 'Enter') {
                    searchCustomBtn.click();
                }
            });
        }
        
        // Bind refresh button event
        const refreshBtn = document.getElementById('refresh-images-btn');
        if (refreshBtn) {
            refreshBtn.addEventListener('click', () => {
                if (this.currentCharacterName) {
                    // Use the current description from the search box if available
                    const customDesc = customDescInput ? customDescInput.value.trim() : null;
                    
                    this.loadImagesForCharacter(
                        this.currentCharacterName, 
                        true, // refresh
                        customDesc || null
                    );
                }
            });
        }
        
        // Check if we have images
        if (!data.images || data.images.length === 0) {
            const noImagesElement = document.createElement('div');
            noImagesElement.className = 'no-images text-center py-5';
            noImagesElement.innerHTML = `
                <p>No images found for ${data.character_name || 'this character'}.</p>
            `;
            this.galleryContainer.appendChild(noImagesElement);
            return;
        }
        
        // Create gallery container
        const galleryGrid = document.createElement('div');
        galleryGrid.className = 'gallery-grid';
        this.galleryContainer.appendChild(galleryGrid);
        
        // Add images to gallery - load each image independently
        data.images.forEach(image => {
            const galleryItem = document.createElement('div');
            galleryItem.className = 'gallery-item';
            galleryItem.dataset.description = image.description || image.title || '';
            
            // Create image element independently
            const imgElement = document.createElement('img');
            imgElement.alt = image.title || 'Character image';
            
            // Set up onerror before setting src
            imgElement.onerror = function() {
                this.onerror = null;
                this.src = '/static/images/image-placeholder.png';
                // Fallback if png doesn't exist
                this.onerror = function() {
                    this.src = '/static/images/image-placeholder.svg';
                };
            };
            
            // Add the description and other elements
            galleryItem.innerHTML = `
                <div class="zoom-icon">
                    <i class="fas fa-search-plus"></i>
                </div>
                <div class="image-description">
                    <p>${image.description || image.title || ''}</p>
                    <button class="btn btn-secondary btn-sm send-to-chat">Ask About This...</button>
                </div>
            `;
            
            // Insert the image as the first child
            galleryItem.insertBefore(imgElement, galleryItem.firstChild);
            
            // Add to the gallery immediately
            galleryGrid.appendChild(galleryItem);
            
            // Set the src last to trigger loading (after the item is in DOM)
            imgElement.src = image.url;
        });
        
        // Rebind events for newly added elements
        this.bindStaticEvents();
    }

    showLargeImage(src, alt) {
        this.modalImage.src = src;
        this.modalImage.alt = alt;
        this.imageModal.show();
    }

    sendToChat(description) {
        if (this.chatController) {
            // Clean up the description: remove HTML tags if present
            let cleanDescription = description;
            
            // Remove HTML tags if present - extract just the text content
            if (description.includes('<p>')) {
                // Create a temporary element to parse the HTML
                const tempDiv = document.createElement('div');
                tempDiv.innerHTML = description;
                cleanDescription = tempDiv.textContent || tempDiv.innerText || description;
            }
            
            // Simulate typing in the chat input
            const chatInput = document.getElementById('chat-input');
            if (chatInput) {
                chatInput.value = `Tell me about ${cleanDescription}`;
                
                // Trigger input event for height adjustment
                const inputEvent = new Event('input', { bubbles: true });
                chatInput.dispatchEvent(inputEvent);
                
                // Trigger the chat send button
                const sendButton = document.getElementById('send-button');
                if (sendButton) {
                    sendButton.click();
                }
            }
        }
    }

    showDescription(description) {
        // You could add additional visual feedback here if needed
        console.log('Description:', description);
    }
}